import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { CourseModule } from '../types'

const courseRoutes: Record<string, string> = {
  'course-investing-essentials.html': '/courses/investing-essentials',
  'course-money-life-planning.html': '/courses/money-life-planning',
  'course-taxes-accounts.html': '/courses/taxes-accounts',
  'course-strategies-portfolios.html': '/courses/strategies-portfolios',
  'course-real-estate.html': '/courses/real-estate',
  'course-retirement.html': '/courses/retirement',
  'course-estate-life-events.html': '/courses/estate-life-events',
  'course-advice-plans.html': '/courses/advice-plans',
  'glossary.html': '/reference/glossary',
  'tools.html': '/reference/tools',
  'resources.html': '/reference/resources',
  'index.html': '/',
}

interface ModuleFrameProps {
  module: CourseModule
  requestedSectionId?: string
  onSectionChange: (index: number) => void
  onSectionTextChange: (text: string) => void
  onQuizPassed: () => void
  sectionRequest: number
}

export function ModuleFrame({ module, requestedSectionId, onSectionChange, onSectionTextChange, onQuizPassed, sectionRequest }: ModuleFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const navigate = useNavigate()
  const [height, setHeight] = useState(900)
  const cleanupRef = useRef<() => void>(() => undefined)

  const activate = useCallback((index: number) => {
    const doc = iframeRef.current?.contentDocument
    const tab = doc?.querySelectorAll<HTMLButtonElement>('.tabbar .tab')[index]
    tab?.click()
  }, [])

  useEffect(() => {
    if (sectionRequest >= 0) activate(sectionRequest)
  }, [sectionRequest, activate])

  const onLoad = useCallback(() => {
    cleanupRef.current()
    const frame = iframeRef.current
    const doc = frame?.contentDocument
    const win = frame?.contentWindow
    if (!doc || !win) return

    doc.body.classList.add('vite-embedded')
    const style = doc.createElement('style')
    style.dataset.viteEmbed = 'true'
    style.textContent = `
      .top,.guide,footer.site,.to-top,.global-financial-disclaimer{display:none!important}
      html{scroll-behavior:auto!important}
      body.vite-embedded{min-height:0!important;background:var(--bg)!important;padding:0!important}
      body.vite-embedded .wrap{max-width:none!important;padding:4px 8px 18px!important}
      body.vite-embedded .mhero{margin-top:0!important}
      body.vite-embedded .tabs-shell{margin-bottom:10px!important}
      body.vite-embedded .disclaim{margin-bottom:0!important}
      @media(max-width:600px){body.vite-embedded .wrap{padding:2px 2px 14px!important}}
    `
    doc.head.appendChild(style)
    doc.documentElement.dataset.theme = document.documentElement.dataset.theme ?? 'light'

    const panels = [...doc.querySelectorAll<HTMLElement>('.tabs-shell:not([hidden]) .panel')]
    const tabs = [...doc.querySelectorAll<HTMLButtonElement>('.tabbar .tab')]
    const select = doc.querySelector<HTMLSelectElement>('#tabsel')
    let activeIndex = Math.max(0, panels.findIndex((panel) => !panel.hidden))

    const updateHeight = () => {
      const next = Math.max(500, Math.min(20_000, doc.documentElement.scrollHeight + 8))
      setHeight(next)
    }
    const publish = () => {
      const next = Math.max(0, panels.findIndex((panel) => !panel.hidden))
      if (next !== activeIndex) activeIndex = next
      onSectionChange(activeIndex)
      onSectionTextChange((panels[activeIndex]?.innerText ?? '').replace(/\s+/g, ' ').trim())
      updateHeight()
    }
    const tabListeners = tabs.map((tab) => {
      const listener = () => window.setTimeout(publish, 0)
      tab.addEventListener('click', listener)
      return [tab, listener] as const
    })
    const onSelect = () => window.setTimeout(publish, 0)
    select?.addEventListener('change', onSelect)
    panels.forEach((panel) => panel.addEventListener('inv-show', publish))

    const requestedIndex = requestedSectionId
      ? module.sections.findIndex((section) => section.sectionId === requestedSectionId)
      : -1
    if (requestedIndex >= 0) tabs[requestedIndex]?.click()
    else publish()

    const clickHandler = (event: MouseEvent) => {
      const link = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]')
      if (!link) return
      const href = link.getAttribute('href') ?? ''
      if (/^(https?:|mailto:|tel:|#)/.test(href)) return
      const [file, hash] = href.split('#')
      let route = courseRoutes[file]
      const moduleMatch = file.match(/^(INV-\d{3})\.html$/)
      if (moduleMatch) route = `/modules/${moduleMatch[1]}`
      if (!route) return
      event.preventDefault()
      navigate(hash ? `${route}#${hash}` : route)
      window.scrollTo({ top: 0, behavior: 'auto' })
    }
    doc.addEventListener('click', clickHandler)

    let quizPassed = false
    const observer = new MutationObserver(() => {
      updateHeight()
      if (quizPassed) return
      const score = doc.querySelector('#quizScore')?.textContent?.toLowerCase() ?? ''
      if (/pass|complete/.test(score)) {
        quizPassed = true
        onQuizPassed()
      }
    })
    observer.observe(doc.body, { subtree: true, childList: true, characterData: true, attributes: true })
    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(updateHeight)
      : null
    resizeObserver?.observe(doc.body)
    win.addEventListener('resize', updateHeight)
    window.setTimeout(updateHeight, 100)

    cleanupRef.current = () => {
      for (const [tab, listener] of tabListeners) tab.removeEventListener('click', listener)
      select?.removeEventListener('change', onSelect)
      doc.removeEventListener('click', clickHandler)
      observer.disconnect()
      resizeObserver?.disconnect()
      win.removeEventListener('resize', updateHeight)
    }
  }, [module, navigate, onQuizPassed, onSectionChange, onSectionTextChange, requestedSectionId])

  useEffect(() => () => cleanupRef.current(), [])

  return (
    <iframe
      ref={iframeRef}
      className="module-frame"
      src={module.legacyUrl}
      title={`${module.id}: ${module.title}`}
      onLoad={onLoad}
      style={{ height }}
    />
  )
}
